"use client";

import { Button } from "@papier/ui/components/ui/button";
import { Input } from "@papier/ui/components/ui/input";
import { Label } from "@papier/ui/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@papier/ui/components/ui/select";
import { Switch } from "@papier/ui/components/ui/switch";
import { Textarea } from "@papier/ui/components/ui/textarea";
import { Separator } from "@papier/ui/components/ui/separator";
import { Plus, Settings, Trash2 } from "lucide-react";
import { SchemaField } from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";
import { createDefaultField } from "@/utils/schema-utils";
import { ExampleEditor } from "../example-editors/example-editor";
import { fieldTypes } from "@/constants/field-types";

interface PropertiesPanelProps {
  selectedField: SchemaField;
  onUpdateField: (id: string, updates: Partial<SchemaField>) => void;
  onUpdateArrayItemType: (arrayId: string, newType: SchemaField["type"]) => void;
}

export function PropertiesPanel({ selectedField, onUpdateField }: PropertiesPanelProps) {
  return (
    <div className="w-96 overflow-y-auto border-l p-4 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Settings className="h-5 w-5" />
        <h3 className="font-semibold">Field Properties</h3>
      </div>

      <div className="space-y-6">
        <div>
          <Label htmlFor="field-name" className="mb-2">
            Field Name
          </Label>
          <Input
            id="field-name"
            value={selectedField.name}
            onChange={(e) => onUpdateField(selectedField.id, { name: e.target.value })}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="field-type" className="mb-2">
            Field Type
          </Label>
          <Select
            value={selectedField.type}
            onValueChange={(value) => {
              const newType = value as SchemaField["type"];
              const newField = createDefaultField(newType, selectedField.name);
              onUpdateField(selectedField.id, {
                type: newType,
                examples: newField.examples,
                enumValues: newField.enumValues,
                arrayItemType: newField.arrayItemType,
                objectFields: newField.objectFields,
              });
            }}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fieldTypes.map((fieldType) => (
                <SelectItem key={fieldType.value} value={fieldType.value}>
                  <fieldType.icon className="mr-2 h-4 w-4" />
                  {fieldType.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="required"
            checked={selectedField.required}
            onCheckedChange={(checked) => onUpdateField(selectedField.id, { required: checked })}
          />
          <Label htmlFor="required">Required field</Label>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Describe this field..."
            value={selectedField.description || ""}
            onChange={(e) => onUpdateField(selectedField.id, { description: e.target.value })}
            className="mt-1"
          />
        </div>

        {selectedField.type === "enum" && (
          <div>
            <Label>Enum Values</Label>
            <div className="mt-2 space-y-2">
              {selectedField.enumValues?.map((value, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={value}
                    onChange={(e) => {
                      const newValues = [...(selectedField.enumValues || [])];
                      newValues[index] = e.target.value;
                      onUpdateField(selectedField.id, { enumValues: newValues });
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newValues = selectedField.enumValues?.filter((_, i) => i !== index);
                      onUpdateField(selectedField.id, { enumValues: newValues });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newValues = [
                    ...(selectedField.enumValues || []),
                    `option${(selectedField.enumValues?.length || 0) + 1}`,
                  ];
                  onUpdateField(selectedField.id, { enumValues: newValues });
                }}
                className="w-full"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Option
              </Button>
            </div>
          </div>
        )}

        <Separator />

        <ExampleEditor field={selectedField} onUpdateField={onUpdateField} />
      </div>
    </div>
  );
}
